import { acceptHMRUpdate, defineStore } from 'pinia'
import axios from 'axios'
import pick from 'lodash/pick'
import siteConfig from '@/config.json'

import { getClassAgeEligibility, timestampToShortDateText } from '@/helpers/datetime'
import { ticketsRemaining } from '@/helpers/tickets'
import {
  getProcessingFeeTitle,
  getProcessingFeeSubtitle,
  getProcessingFeeGLFields,
  rateProcessingFee,
} from '@/helpers/payments'

import { useClassStore } from '@/stores/ClassStore'
import { useUserStore } from '@/stores/UserStore'
import { useEventStore } from '@/stores/EventStore'
import { useAlternativeShopStore } from '@/stores/AlternativeShopStore'
import { useCommon } from '@/hooks/useCommon'
import giftImage from '@/assets/images/gift.png'

const classKey = (item) => {
  return `${item.catalogId}:${item.personId}:${item.activityDayId}`
}

const hasShippingCategory = (item) =>
  item.shippingCategory !== null &&
  item.shippingCategory !== undefined &&
  item.shippingCategory !== '' &&
  item.shippingCategory !== 0

const classLocationLine = (cls) =>
  siteConfig.features?.includeClassLocationInReceipt && cls?.location && cls.location !== 'None assigned'
    ? `Location: ${cls.location}`
    : ''

const classInstructorLine = (cls) => (cls?.instructorName ? `Instructor: ${cls.instructorName}` : '')

const classSubtitle = (cls, dateParts, { waitlist = false, includeInstructor = true } = {}) =>
  [waitlist ? 'Wait-list' : '', ...dateParts, includeInstructor ? classInstructorLine(cls) : '', classLocationLine(cls)]
    .filter(Boolean)
    .join('<br>')

// Blank attendee forces a choice at checkout. Members-only classes do the same when the buyer is not a member.
const classAttendeeId = (userStore, { membersOnly = false } = {}) => {
  if (siteConfig.features?.requireExplicitAttendeeSelection) return null
  if (membersOnly && !userStore.isActiveMember) return null
  return userStore.account?.personId
}

// fields addTicket sets - anything else on a cart ticket is derived and must not be cloned
const TICKET_FIELDS = [
  'type',
  'code',
  'scheduleId',
  'eventId',
  'ticketId',
  'itemId',
  'personId',
  'glacct',
  'glclass',
  'glsubclass',
  'title',
  'dateLabel',
  'timeLabel',
  'tierName',
  'subtitle',
  'memberCost',
  'nonMemberCost',
  'imageUrl',
  'taxable',
  'salesTaxRate',
]

const guestTicketsEnabled = () => !!siteConfig.features?.events?.guestTickets

const ticketSessionKey = (item) => `${item.eventId}:${item.scheduleId}`

// with guest tickets, purchaser is the first attendee for each event session
// every later ticket for that session is a guest
const labelGuestTickets = (items, account) => {
  const purchaserName = [account?.firstName, account?.lastName].filter(Boolean).join(' ').trim()
  const seenSessions = new Set()
  items
    .filter((item) => item.type === 'ticket')
    .forEach((item) => {
      const key = ticketSessionKey(item)
      item.isGuestTicket = seenSessions.has(key)
      seenSessions.add(key)
      if (account?.personId) item.personId = account.personId
      item.guestName = purchaserName && item.isGuestTicket ? `Guest of ${purchaserName}` : purchaserName
    })
}

const baseCost = (item) => {
  if (item.payDepositOnly) {
    return parseFloat(item.nonMemberDeposit) || 0
  }
  const cost = parseFloat(item.nonMemberCost) || 0
  return item.fee ? cost + (parseFloat(item.fee) || 0) : cost
}

const isDiscountMemberForItem = (item, membershipsInCart, userStore) => {
  if (userStore.isActiveMemberWithDiscount(item.personId)) return true
  if (!membershipsInCart?.length) return false
  // Config uses booleans; Canvas API / MySQL use 0|1. Accept both.
  return membershipsInCart.some(
    (membership) =>
      ((membership.household === true || membership.household === 1) && membership.discounts) ||
      (membership.personId === item.personId && membership.discounts)
  )
}

// dynamicMemberPrice sites: no tier % for this item type means no member price (don't fall back to catalog cost_member)
const membershipAdjustedCost = (item, discountPercentage) => {
  if (discountPercentage) {
    return parseFloat(item.nonMemberCost) * (1 - discountPercentage / 100)
  }
  if (siteConfig.features.dynamicMemberPrice) {
    return parseFloat(item.nonMemberCost)
  }
  return parseFloat(item.memberCost)
}

export const useCartStore = defineStore('CartStore', {
  state: () => {
    return {
      items: [],
      loading: false,
      discountCode: null,
      discounts: [],
      salesItems: [],
      openedCart: false,
      cartNotices: [],
      shippingItemPrice: 0,
      shippingItemCategory: 0,
      shipping: 0,
      shouldPickupItems: false,
      campaign: null,
      campaignId: null,
      prereqMatched: false,
    }
  },

  getters: {
    checkoutNotAllowedMessage() {
      if (siteConfig.features.requireUniqueAttendeesForSameClass) {
        const dupes = this.items.filter((item) => item.duplicateAttendee)
        if (dupes.length) {
          return 'You have the same person in the same class more than once. Please remove the duplicates.'
        }
      }
      if (siteConfig.features.enforceClassAgeRestrictions) {
        const ageStatuses = this.items.map((item) => this.classAgeError(item)).filter(Boolean)
        if (ageStatuses.length) {
          return ageStatuses.join(' ')
        }
      }
      for (let item of this.items) {
        if (
          item.type !== 'account-payment' &&
          item.type !== 'donation' &&
          item.type !== 'inventory' &&
          !(item.type === 'ticket' && guestTicketsEnabled()) &&
          !item.hidePersonPicker &&
          !item.personId
        ) {
          return 'Please select a person for each item where required.'
        }
      }

      const soldOutStatuses = this.items.map((item) => this.soldOutStatus(item)).filter(Boolean)

      if (soldOutStatuses.length) {
        return soldOutStatuses.join(' ')
      }
      return ''
    },
    checkoutAllowed() {
      return (
        !this.checkoutNotAllowedMessage && (this.prereqMatched || this.items.every((item) => !item.classPrerequisite))
      )
    },

    shouldNotAddDonationAutomatically() {
      return sessionStorage.getItem('shouldNotAddDonationAutomatically')
    },

    donationAmount() {
      const donationItemAmount = this.itemsForAccount.find((item) => item.type === 'donation')
      return donationItemAmount ? donationItemAmount.effectiveCost : 0
    },

    donationFrequency() {
      const donationItemFrequency = this.itemsForAccount.find((item) => item.type === 'donation')
      return donationItemFrequency?.frequency
    },

    haveMembershipInCart() {
      let membershipInCart = this.items.find((item) => item.type === 'membership')
      if (membershipInCart) return true
      return false
    },
    canAddMembersOnlyItem() {
      const userStore = useUserStore()
      return this.haveMembershipInCart || !!(userStore.account && userStore.memberWithActiveMembership)
    },
    membershipsInCart() {
      return this.items.filter((item) => item.type === 'membership')
    },
    membershipAutoRenew() {
      return this.itemsForAccount.some((item) => item.type === 'membership' && item.autoRenew)
    },
    shippableInCart() {
      return this.items.some((item) => item.type === 'inventory' && hasShippingCategory(item))
    },
    classPersonCounts() {
      const counts = {}
      this.items.forEach((item) => {
        if (item.type === 'class' && item.personId) {
          const key = classKey(item)
          counts[key] = counts[key] || 0
          counts[key] += 1
        }
      })
      return counts
    },
    itemsForAccount() {
      const userStore = useUserStore()

      if (guestTicketsEnabled()) {
        labelGuestTickets(this.items, userStore.account)
      }

      const items = this.items.map((item) => {
        const isDiscountMember = isDiscountMemberForItem(item, this.membershipsInCart, userStore)
        const discountPercentage = userStore.discountPercentForUser(item.personId, item.type)
        if (isDiscountMember) {
          item.effectiveCost = membershipAdjustedCost(item, discountPercentage)
          item.membershipDiscount = parseFloat(item.nonMemberCost) - item.effectiveCost
        } else {
          item.effectiveCost = parseFloat(item.nonMemberCost)
          item.membershipDiscount = 0
        }

        item.effectiveCostWithoutFees = item.effectiveCost

        if (item.fee) {
          item.effectiveCost += item.fee
        }

        // clear any coupon fields from a previously applied code before applying the current one
        delete item.discount
        delete item.exception
        delete item.notes
        delete item.discountGL
        delete item.discountGlclass
        delete item.discountGLsubclass

        if (!isDiscountMember || !siteConfig.features.dontStackDiscounts) {
          // find discount for this item
          const discount = this.discounts.find((discount) => discount.itemLinkId === item.itemLinkId)
          if (discount) {
            item.effectiveCost = Math.max(0, item.effectiveCost - discount.discount)
            item.discount = discount.discount
            item.exception = discount.discount
            item.notes = discount.itemNotes
            item.discountGL = discount.discountGL
            item.discountGlclass = discount.discountGlclass
            item.discountGLsubclass = discount.discountGLsubclass
          }
        }

        if (item.type === 'class') {
          const key = classKey(item)
          item.duplicateAttendee = item.personId && this.classPersonCounts[key] > 1
        }

        if (item.payDepositOnly) {
          item.effectiveCostWithoutDeposit = item.effectiveCost
          if (isDiscountMember) {
            item.effectiveCost = parseFloat(item.memberDeposit)
            item.membershipDiscount = parseFloat(item.nonMemberDeposit) - item.effectiveCost
          } else {
            item.effectiveCost = parseFloat(item.nonMemberDeposit)
            item.membershipDiscount = 0
          }
        }

        if (item.taxable) {
          const taxableAmount = !!siteConfig.features.excludeFeesFromTaxes?.includes(item.type)
            ? item.effectiveCostWithoutFees
            : item.effectiveCost
          item.tax = (taxableAmount * item.salesTaxRate || 0) / 100
        }

        item.totalCost = item.effectiveCost + (item.tax || 0)
        return item
      })

      return items
    },

    total() {
      let total = this.itemsForAccount.reduce((total, item) => total + (item.totalCost || 0), 0)
      if (this.shipping && !this.shouldPickupItems) {
        total += this.shipping
      }

      return total
    },
    subtotal() {
      let total = this.itemsForAccount.reduce((total, item) => total + baseCost(item) + (item.tax || 0), 0)
      if (this.shipping && !this.shouldPickupItems) {
        total += this.shipping
      }

      return total
    },
    classesSubtotal() {
      return this.itemTypeSubtotal('class')
    },
    classesTotal() {
      let total = 0
      this.itemsForAccount.filter((item) => item.type === 'class').forEach((item) => (total += item.effectiveCost))
      return total
    },
    inventoryTotal() {
      let total = 0
      this.itemsForAccount.filter((item) => item.type === 'inventory').forEach((item) => (total += item.effectiveCost))
      return total
    },
    chargesTotal() {
      let total = 0
      this.itemsForAccount
        .filter((item) => item.type === 'charge' && item.feeType !== 'processingFee')
        .forEach((item) => (total += item.effectiveCost))
      return total
    },
    chargesSubtotal() {
      return this.items
        .filter((item) => item.type === 'charge' && item.feeType !== 'processingFee')
        .reduce((total, item) => total + baseCost(item), 0)
    },
    membershipsTotal() {
      let total = 0
      this.itemsForAccount.filter((item) => item.type === 'membership').forEach((item) => (total += item.effectiveCost))
      return total
    },
    donationsTotal() {
      let total = 0
      this.itemsForAccount.filter((item) => item.type === 'donation').forEach((item) => (total += item.effectiveCost))

      return total
    },
    ticketsTotal() {
      let total = 0
      this.itemsForAccount.filter((item) => item.type === 'ticket').forEach((item) => (total += item.effectiveCost))
      return total
    },
    processingFeeTotal() {
      let total =
        this.classesTotal +
        this.inventoryTotal +
        this.chargesTotal +
        this.membershipsTotal +
        this.accountPaymentsTotal +
        this.ticketsTotal +
        this.certificatesTotal +
        (siteConfig.features.donationProcessingFee ? this.donationsTotal : 0)
      const flatFee = siteConfig.features.processingFeeFlat
      const rate = siteConfig.features.processingFeeRate
      let fee = 0
      if (flatFee) {
        fee = total > 0 ? flatFee : 0
      } else if (rate) {
        fee = rateProcessingFee(total, rate)
      }
      return fee
    },
    accountPaymentsTotal() {
      let total = 0
      this.itemsForAccount
        .filter((item) => item.type === 'account-payment')
        .forEach((item) => (total += item.effectiveCost))
      return total
    },
    certificatesTotal() {
      let total = 0
      this.itemsForAccount
        .filter((item) => item.type === 'gift-certificate')
        .forEach((item) => (total += item.effectiveCost))
      return total
    },
    itemTypeTotal() {
      return (type) =>
        this.itemsForAccount.filter((item) => item.type === type).reduce((total, item) => total + item.effectiveCost, 0)
    },
    itemTypeSubtotal() {
      return (type) =>
        this.items.filter((item) => item.type === type).reduce((total, item) => total + baseCost(item), 0)
    },
    itemTypeMembershipDiscount() {
      return (type) =>
        this.itemsForAccount
          .filter((item) => item.type === type)
          .reduce((total, item) => total + (item.membershipDiscount || 0), 0)
    },
    itemTypes() {
      return [...new Set(this.itemsForAccount.map((item) => item.type))]
    },
    discountsByType() {
      const typeByLinkId = {}
      this.itemsForAccount.forEach((item) => {
        typeByLinkId[item.itemLinkId] = item.type
      })
      return this.discounts.reduce((grouped, discount) => {
        const type = typeByLinkId[discount.itemLinkId]
        if (type) {
          grouped[type] = grouped[type] || []
          grouped[type].push(discount)
        }
        return grouped
      }, {})
    },
    taxes() {
      return this.itemsForAccount.reduce((total, item) => total + (item.tax || 0), 0)
    },
  },

  actions: {
    init() {
      this.restoreCart()
      this.filterOutBadItems()
      this.calculateShippingPrice()
    },
    itemCost(item) {
      const userStore = useUserStore()
      if (!isDiscountMemberForItem(item, this.membershipsInCart, userStore)) {
        return parseFloat(item.nonMemberCost)
      }
      const discountPercentage = userStore.discountPercentForUser(item.personId, item.type)
      return membershipAdjustedCost(item, discountPercentage)
    },
    classAgeWarning(item) {
      const result = this.classAgeEligibility(item)
      if (!result || result.eligibility === 'ok') return null
      return result.message
    },
    classAgeError(item) {
      if (!siteConfig.features.enforceClassAgeRestrictions) return null
      const result = this.classAgeEligibility(item)
      if (!result || result.eligibility !== 'block') return null
      return result.message
    },
    classAgeEligibility(item) {
      if (item.type !== 'class') return null
      if (!item.personId) return null

      const person = useUserStore().userProfile(item.personId)
      const cls = useClassStore().loadedClassById(item.catalogId)

      const requireDob =
        !siteConfig.features.hideDateOfBirthFieldsOnRegistration && !siteConfig.features.dateOfBirthIsOptionalInProfiles

      if (!person?.dob) {
        if (!requireDob) return null
        const name = [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim() || 'this student'
        return {
          eligibility: 'block',
          message: `Please add a date of birth (dd/mm/yyyy) for ${name} before registering for this class.`,
        }
      }

      const eligibility = getClassAgeEligibility(person.dob, cls, siteConfig.features?.classAgeRestrictionMargin)
      if (eligibility === 'ok') return { eligibility, message: null }

      return {
        eligibility,
        message: `${cls.shortTitle} is for ${cls.ages.toLowerCase()}`,
      }
    },
    soldOutStatus(item) {
      if (item.type !== 'ticket') return null
      const event = useEventStore().events?.find((e) => e.id === item.eventId)

      if (!event) return null

      const ok = this.ticketAvailableIncludingCart(event, item.scheduleId)
      return ok ? null : 'Sold out: there are not enough tickets available'
    },
    itemCodeToItem(code) {
      return this.salesItems.find((item) => item.item_code === code)
    },
    exhibitionSubmissions() {
      return this.items.filter((item) => item.type === 'exhibition')
    },
    getExhibitionSubmission(id) {
      return this.items.find((item) => item.type === 'exhibition' && item.id == id)
    },
    async updateShouldPickupItems(value) {
      this.shouldPickupItems = value
      await this.calculateShippingPrice()
    },
    updateShouldNotAddDonationAutomatically(value = true) {
      sessionStorage.setItem('shouldNotAddDonationAutomatically', value)
    },
    donationItem() {
      return this.items.find((item) => item.type === 'donation')
    },
    updateDonationsTotal(donation) {
      let donationItem = donation || this.donationItem()
      const { getImageUrl } = useCommon()

      if (!donationItem) return
      donation = { ...donationItem, ...this.campaign }
      donation.imageUrl = getImageUrl(false, siteConfig.assets.donationImage)
      donation.frequency = donation.frequency || 'one-time'
      if (!this.donationItem()) {
        this.addItem(donation)
      } else {
        this.items = this.items.reduce((acc, item) => {
          if (item.type === 'donation') {
            item = { ...item, ...donation }
          }

          acc.push(item)
          return acc
        }, [])
      }

      this.saveItemsToLocalStorge()
    },
    updateCampaign(campaign) {
      this.campaign = { ...campaign }
      this.updateDonationsTotal()
    },
    async fetchSalesItems() {
      try {
        const response = await axios.get(`${siteConfig.apiBaseUrl}/reports/queries/salesitem-quantities`)
        if (response.data.ok) {
          this.salesItems = response.data.results
        }
      } catch (e) {
        console.log('fetchSalesItems error', e)
      }
    },

    removeDiscountCode() {
      this.discountCode = null
      this.discounts = []
    },

    setLocalDiscountCode(code) {
      localStorage.setItem(`${siteConfig.key}Code`, JSON.stringify(code))
    },

    refreshDiscountCode() {
      const codeRaw = localStorage.getItem(`${siteConfig.key}Code`)
      const code = JSON.parse(codeRaw)

      if (code) {
        this.applyDiscountCode(code)
      }
    },

    async applyDiscountCode(code) {
      this.removeDiscountCode()
      if (!code) return
      this.discountCode = code
      const itemCodeMap = {
        class: 'CATALOG',
        inventory: 'INVENTORY',
        membership: 'MEMBERSHIP',
        exhibition: 'EXFEE',
        ticket: 'TICKET',
        donation: 'DONATION',
        charge: 'OTHERCHARGE',
        'account-payment': 'ACCTPAYMENT',
        rental: 'RENTAL',
      }
      const items = {
        items: this.items
          .filter((item) => itemCodeMap[item.type])
          .map((item) => {
            const itemType = itemCodeMap[item.type]
            return {
              discountCartItem: {
                itemLinkId: item.itemLinkId,
                registrantId: item.personId,
                ownerId: item.personId,
                itemType: itemType,
                itemId: item.itemId ?? item.inventoryId,
                scheduleId: item.activityDayId,
                coupon: code,
                price: this.itemCost(item),
                itemQuantity: parseInt(item.quantity, 10),
              },
            }
          }),
      }

      let data
      try {
        const response = await axios.post(`${siteConfig.apiBaseUrl}/discount-cart-items`, items)
        data = response.data
      } catch (e) {
        console.log('applyDiscountCode error', e)
      }
      if (!data?.ok) {
        this.removeDiscountCode()
        return "We couldn't apply this discount code right now. Please try again."
      }
      this.discounts =
        data.data
          ?.map((discount) => ({ ...discount, discount: parseFloat(discount.discount) || 0 }))
          .filter((discount) => discount.discount > 0) || []
      return this.discounts.length
        ? 'Discount code applied successfully!'
        : 'No items in cart are eligible for this discount code'
    },

    restoreCart() {
      const cartRaw = localStorage.getItem(`${siteConfig.key}Cart`)
      if (!cartRaw) return
      try {
        const cart = JSON.parse(cartRaw)
        if (cart.items) {
          this.items = cart.items
        }
      } catch (error) {
        console.log('error restoring cart', error)
      }
    },
    saveItemsToLocalStorge() {
      localStorage.setItem(
        `${siteConfig.key}Cart`,
        JSON.stringify({
          items: this.items.filter((item) => item.type !== 'exhibition'), // exhibit item images are too large
          discountCode: this.discountCode,
        })
      )
    },
    async calculateShippingPrice() {
      const previous = this.shipping
      let newShipping = 0

      let shippingCategories = this.items
        .filter((item) => item.type === 'inventory' && hasShippingCategory(item))
        .map((item) => item.shippingCategory)
      if (shippingCategories.length > 0) {
        try {
          let result = await axios.get(
            `${siteConfig.apiBaseUrl}/salesitems/getshippingcost/${shippingCategories.join(',')}`
          )
          if (result.data.ok) {
            newShipping = Number(result.data.results)
          }
        } catch (error) {
          console.log('error calculating shipping price', error)
        }
      }
      if (this.shouldPickupItems) {
        newShipping = 0
      }
      if (newShipping !== previous) {
        this.shipping = newShipping
      }
    },
    addItem(item) {
      let addedAt = new Date()
      let cartId = Math.floor(100000000000000 + Math.random() * 900000000000000)
      const itemLinkId = cartId
      const { getImageUrl } = useCommon()
      const imageUrl = getImageUrl(false)
      const cartItem = { imageUrl: imageUrl, ...item, cartId: cartId, itemLinkId: itemLinkId, addedAt: addedAt }
      this.items.push(cartItem)
      this.saveItemsToLocalStorge()
      return cartId
    },
    updatePersonIdForItem({ cartId, personId, name }) {
      const item = this.items.find((item) => item.cartId === cartId)
      if (item) {
        item.personId = personId
        if (item.type === 'ticket') {
          item.guestName = name
        }
      }
      this.updateRegistrationFees()
      this.saveItemsToLocalStorge()
    },
    assignAccountPersonIdToItems() {
      // classes added to the cart while logged out have no personId
      // once a user logs in, default them to the account holder
      if (siteConfig.features?.requireExplicitAttendeeSelection) return

      const userStore = useUserStore()
      if (!userStore.account?.personId) return

      this.items
        .filter((item) => item.type === 'class' && !item.personId && (!item.membersOnly || userStore.isActiveMember))
        .forEach((item) => {
          item.personId = userStore.account.personId
        })

      this.updateRegistrationFees()
      this.saveItemsToLocalStorge()
    },
    addExhibitionSubmission(json_data) {
      const userStore = useUserStore()
      // data is sent as json string because of the imageUrl which is a large base64 string
      const data = JSON.parse(json_data)
      const item = {
        ...data,
        type: 'exhibition',
        eventId: data.exhibit.id,
        itemId: data.exhibit.id,
        operator: userStore.account?.firstName + ' ' + userStore.account?.lastName,
        personId: userStore.account?.personId,
        subtitle: `Submission to: ${data.exhibit.title}<br>
                  Dates:
                  ${data.exhibit.start_date_fmt} -
                  ${data.exhibit.end_date_fmt}<br>
                  Price: $${data.price}<br>
                  Dimensions: ${data.dimensions}`,
      }
      this.addItem(item)
      this.updateExhibitionPricing()
      return true
    },

    // For each exhibit item if there is another of the same type in the cart, set the price to the price2 value otherwise set it to the price value
    updateExhibitionPricing() {
      const items = this.items.filter((item) => item.type === 'exhibition')
      // group all items by eventId
      const groupedItems = {}
      items.forEach((item) => {
        if (!groupedItems[item.eventId]) {
          groupedItems[item.eventId] = []
        }
        groupedItems[item.eventId].push(item)
      })
      // for each group set the price for all but the first to price2
      Object.values(groupedItems).forEach((group) => {
        group?.forEach((item, index) => {
          if (index > 0) {
            item.nonMemberCost = item.exhibit.fee2
            item.memberCost = item.exhibit.memFee2
          } else {
            item.nonMemberCost = item.exhibit.fee1
            item.memberCost = item.exhibit.memFee1
          }
        })
      })
      this.saveItemsToLocalStorge()
    },
    duplicatesOfClass(cls, scheduleId = null) {
      if (!cls.catalogId) {
        return
      }
      if (scheduleId) {
        return this.items.filter((item) => item.activityDayId === scheduleId)
      } else {
        return this.items.filter((item) => item.catalogId === cls.catalogId)
      }
    },
    seatAvailableIncludingCart(cls, scheduleId = null) {
      if (scheduleId) {
        const classDay = cls.classDays.find((d) => d.scheduleId === scheduleId)
        const seatsLeft = classDay.limit - classDay.daysSold - this.duplicatesOfClass(cls, scheduleId)?.length || 0 < 0
        return seatsLeft
      } else {
        const seatsLeft = cls.seatsAvailable - this.duplicatesOfClass(cls)?.length || 0 < 0
        return seatsLeft
      }
    },
    addClass(id, opts = {}) {
      const classStore = useClassStore()
      const userStore = useUserStore()

      let classDetails = opts.details
      let override = false

      if (classDetails) {
        override = true
      } else {
        classDetails = classStore.openClasses.find((c) => c.catalogId === id)
      }

      if (!classDetails) {
        return
      }

      if (classDetails.membersOnly && !this.canAddMembersOnlyItem) {
        console.warn('Cannot add members-only class to cart: active membership required', id)
        return
      }

      if (!this.seatAvailableIncludingCart(classDetails)) {
        console.log('No seats available for class', id)
        return
      }

      const subtitle = classSubtitle(classDetails, [classDetails.dateSummary, classDetails.timeSummary])
      const item = {
        ...classDetails,
        type: 'class',
        catalogId: classDetails.catalogId,
        itemId: classDetails.catalogId,
        personId: classAttendeeId(userStore, { membersOnly: classDetails.membersOnly }),
        title: classDetails.shortTitle,
        subtitle: subtitle,
        tuitionId: classDetails.tuitionId,
        glclass: classDetails.glclass,
        glsubclass: classDetails.glsubclass,
        memberCost: classDetails.costMember,
        nonMemberCost: classDetails.costNon,
        membersOnly: classDetails.membersOnly,
        imageUrl: classDetails.imageUrl,
        timeSummary: classDetails.timeSummary,
        dateSummary: classDetails.dateSummary,
        partialSale: classDetails.daySale,
        formData: classDetails.formData,
        taxable: classDetails.taxable,
        salesTaxRate: classDetails.taxrate,
      }

      if (override) {
        item.override = true
      }

      if (classDetails.fee) {
        item.fee = classDetails.fee
        item.feeId = classDetails.feeId
      }
      return this.addItem(item)
    },
    addClassDay(id, scheduleId, details = null) {
      const classStore = useClassStore()
      const userStore = useUserStore()

      let classDetails = details
      let override = false

      if (classDetails) {
        override = true
      } else {
        classDetails = classStore.openClasses.find((c) => c.catalogId === id)
      }

      if (!classDetails) {
        return
      }

      if (classDetails.membersOnly && !this.canAddMembersOnlyItem) {
        console.warn('Cannot add members-only class to cart: active membership required', id)
        return
      }

      const classDay = classDetails.classDays.find((d) => d.scheduleId === scheduleId)

      if (!classDay) {
        console.error('Could not find classDay with scheduleId', scheduleId, 'for catalogId', id)
        return
      }

      if (!this.seatAvailableIncludingCart(classDetails, scheduleId)) {
        console.log('No seats available for class', id, 'session', scheduleId)
        return
      }

      const subtitle = classSubtitle(classDetails, [classDay.dateLabel])

      const item = {
        ...classDetails,
        type: 'class',
        activityDayId: scheduleId,
        catalogId: classDetails.catalogId,
        itemId: classDetails.catalogId,
        personId: classAttendeeId(userStore),
        title: classDetails.shortTitle,
        subtitle: subtitle,
        memberCost: classDetails.sessionCostMem,
        nonMemberCost: classDetails.sessionCostNon,
        membersOnly: classDetails.membersOnly,
        imageUrl: classDetails.imageUrl,
        dateLabel: classDay.dateLabel,
        formData: classDetails.formData,
        taxable: classDetails.taxable,
        salesTaxRate: classDetails.taxrate,
      }

      if (override) {
        item.override = true
      }

      if (classDetails.feePerSession) {
        item.fee = classDetails.feePerSession
        item.feeId = classDetails.feeId
      }

      return this.addItem(item)
    },
    addWaitlistClass(id) {
      const classStore = useClassStore()
      const userStore = useUserStore()

      const classDetails = classStore.openClasses.find((c) => c.catalogId === id)

      if (classDetails.membersOnly && !this.canAddMembersOnlyItem) {
        console.warn('Cannot add members-only class waitlist to cart: active membership required', id)
        return
      }

      const subtitle = classSubtitle(classDetails, [classDetails.dateSummary, classDetails.timeSummary], {
        waitlist: true,
        includeInstructor: false,
      })
      const item = {
        type: 'class',
        catalogId: classDetails.catalogId,
        personId: classAttendeeId(userStore),
        title: classDetails.shortTitle,
        subtitle: subtitle,
        memberCost: 0,
        nonMemberCost: 0,
        imageUrl: classDetails.imageUrl,
        membersOnly: classDetails.membersOnly,
        joinWaitlist: true,
      }
      return this.addItem(item)
    },
    addWaitlistClassDay(id, scheduleId) {
      const classStore = useClassStore()
      const userStore = useUserStore()

      const classDetails = classStore.openClasses.find((c) => c.catalogId === id)

      if (classDetails.membersOnly && !this.canAddMembersOnlyItem) {
        console.warn('Cannot add members-only class waitlist to cart: active membership required', id)
        return
      }

      const classDay = classDetails.classDays.find((d) => d.scheduleId === scheduleId)

      if (!classDay) {
        console.error('Could not find classDay with scheduleId', scheduleId, 'for catalogId', id)
        return
      }
      const subtitle = classSubtitle(classDetails, [classDay.dateLabel], { waitlist: true, includeInstructor: false })
      const item = {
        type: 'class',
        activityDayId: scheduleId,
        catalogId: classDetails.catalogId,
        itemId: classDetails.catalogId,
        personId: classAttendeeId(userStore),
        title: classDetails.shortTitle,
        subtitle: subtitle,
        memberCost: 0,
        nonMemberCost: 0,
        imageUrl: classDetails.imageUrl,
        membersOnly: classDetails.membersOnly,
        joinWaitlist: true,
        dateLabel: classDay.dateLabel,
      }

      return this.addItem(item)
    },
    addMembership(membership, addon, autoRenew = false) {
      const userStore = useUserStore()
      const item = {
        type: 'membership',
        categoryId: membership.categoryId,
        itemId: membership.categoryId,
        discounts: membership.discounts,
        household: membership.household,
        personId: userStore.account?.personId,
        title: membership.title,
        memberCost: membership.price,
        nonMemberCost: membership.price,
        imageUrl: membership.image,
        fee: addon?.price,
        addOnId: addon?.id,
        autoRenew: autoRenew,
        subtitle: autoRenew ? 'Auto Renewing' : '',
        glAccount: membership.accountId,
        glclass: membership.glclass,
        glsubclass: membership.glsubclass,
      }
      return this.addItem(item)
    },
    duplicatesOfTicket(event, scheduleId = null) {
      if (!event?.teventId) return []

      if (scheduleId) {
        return this.items.filter((item) => item.scheduleId === scheduleId)
      } else {
        return this.items.filter((item) => item.itemId === event.teventId)
      }
    },
    ticketAvailableIncludingCart(event, scheduleId = null, fromEventsPage = false) {
      if (!scheduleId) {
        return (event.sessions || []).some((s) => this.ticketAvailableIncludingCart(event, s.scheduleId))
      }

      const remaining = ticketsRemaining(event, scheduleId)
      if (!remaining) return false

      const inCart = this.duplicatesOfTicket(event, scheduleId)?.length || 0

      return fromEventsPage ? inCart < remaining : inCart <= remaining
    },
    addTicket(event, session, tier) {
      const userStore = useUserStore()

      const item = {
        type: 'ticket',
        code: event.code,
        scheduleId: session.scheduleId,
        eventId: event.teventId,
        ticketId: event.ticketIdForScheduleId[session.scheduleId],
        itemId: event.ticketIdForScheduleId[session.scheduleId],
        personId: userStore.account?.personId,
        glacct: event.glacct,
        glclass: event.glclass,
        glsubclass: event.glsubclass,
        title: event.name,
        dateLabel: session.dateLabel,
        timeLabel: session.timeLabel,
        tierName: tier.name,
        subtitle: [timestampToShortDateText(session.date), session.timeLabel, tier.name].filter(Boolean).join(' | '),
        memberCost: tier.cost,
        nonMemberCost: tier.cost,
        imageUrl: event.imageUrl,
        taxable: event.taxable,
        salesTaxRate: event.salesTaxRate,
      }
      return this.addItem(item)
    },
    ticketsForTier({ eventId, scheduleId, tierName }) {
      return this.items.filter(
        (item) =>
          item.type === 'ticket' &&
          item.eventId === eventId &&
          item.scheduleId === scheduleId &&
          item.tierName === tierName
      )
    },
    canAddTicketFromCart(ticket) {
      const event = useEventStore().events?.find((e) => e.id === ticket.eventId)
      return !!event && this.ticketAvailableIncludingCart(event, ticket.scheduleId, true)
    },
    setTicketQty({ ticket, event, session, tier }, qty) {
      const eventId = ticket?.eventId ?? event.teventId
      const scheduleId = ticket?.scheduleId ?? session.scheduleId
      const tierName = ticket?.tierName ?? tier.name
      const loadedEvent = event || useEventStore().events?.find((e) => e.id === eventId)

      let current = this.ticketsForTier({ eventId, scheduleId, tierName })
      qty = Math.max(0, qty)

      while (current.length < qty) {
        // availability is unknown until the event loads, so don't add past it
        if (!loadedEvent || !this.ticketAvailableIncludingCart(loadedEvent, scheduleId, true)) break
        if (event) {
          this.addTicket(event, session, tier)
        } else {
          this.addItem(pick(current[0], TICKET_FIELDS))
        }
        current = this.ticketsForTier({ eventId, scheduleId, tierName })
      }

      // remove the most recently added first so the purchaser's own ticket stays put
      current.slice(qty).forEach((item) => this.removeItem(item.cartId))
    },
    addGiftCertificate(data) {
      const item = {
        type: 'gift-certificate',
        title: `$${data.amount.toFixed(2)} Gift Certificate`,
        subtitle: data.recipientName,
        memberCost: data.amount,
        nonMemberCost: data.amount,
        recipientName: data.recipientName,
        imageUrl: siteConfig.assets.giftCertificateImage
          ? `/sites/${siteConfig.key}/${siteConfig.assets.giftCertificateImage}`
          : giftImage,
        hidePersonPicker: true,
        data,
      }
      return this.addItem(item)
    },
    addAccountPayment(data, member = null) {
      const userStore = useUserStore()
      const person = member || userStore.account

      const item = {
        type: 'account-payment',
        title: member
          ? `Household balance payment for ${member.firstName} ${member.lastName}`
          : 'Account balance payment',
        subtitle: '',
        items: member?.accountItems
          ? member.accountItems.map((ai) => ({
              name: ai.lineItem?.itemName,
              amountDue: (ai.lineItem.currTotalAmt - ai.lineItem.cumulativePaid).toFixed(2),
              glAccount: ai.lineItem.glacct,
              glclass: ai.lineItem.glclass,
              glsubclass: ai.lineItem.glsubclass,
            }))
          : userStore.accountItems?.map((ai) => ({
              name: ai.lineItem?.itemName,
              amountDue: (ai.lineItem.currTotalAmt - ai.lineItem.cumulativePaid).toFixed(2),
            })),
        memberCost: data.amount,
        nonMemberCost: data.amount,
        personId: person.personId, // 👈 important, tie to the household member
        removable: data.removable === false ? false : true,
        data,
      }

      let existing = this.items.find((item) => item.type === 'account-payment' && item.personId === person.personId)
      if (existing) {
        this.removeItem(existing.cartId)
      }

      return this.addItem(item)
    },

    addInventory(data, opts = {}) {
      const userStore = useUserStore()
      const item = {
        type: 'inventory',
        inventoryId: data.inventoryId,
        itemId: data.inventoryId,
        itemCode: data.itemCode,
        glAccount: data.glacct,
        glclass: data.glclass,
        glsubclass: data.glsubclass,
        personId: data.personId || userStore.account?.personId,
        recurring: data.recurring || false,
        title: data.title,
        subtitle: data.subtitle,
        memberCost: data.memberCost,
        nonMemberCost: data.nonMemberCost,
        imageUrl: data.imageUrl,
        parentId: data.parentId,
        removable: data.removable === false ? false : true,
        hidePersonPicker: data.hidePersonPicker,
      }
      return this.addItem(item)
    },

    itemsByTypeId(type, id) {
      return this.items.filter((i) => i.type == type && i.itemId == id)
    },
    itemQty(type, id) {
      return this.itemsByTypeId(type, id).length
    },

    async addItemWithQty(item, qty, maxQty = 999) {
      qty = Math.max(0, Math.min(qty, maxQty))

      const cartItems = this.itemsByTypeId(item.type, item.id)
      const currentQty = cartItems.length

      if (qty === currentQty) return

      if (qty > currentQty) {
        const itemsToAdd = qty - currentQty
        for (let i = 0; i < itemsToAdd; i++) {
          this.addItem(item)
        }
      } else {
        const itemsToRemove = currentQty - qty
        for (let i = 0; i < itemsToRemove && i < cartItems.length; i++) {
          this.removeItem(cartItems[i].cartId)
        }
      }
    },
    async addShopItem(data, qty = 1) {
      const userStore = useUserStore()
      let item = {
        glAccount: data.glacct,
        glclass: data.glclass,
        glsubclass: data.glsubclass,
        itemId: data.id,
        title: data.itemName,
        type: 'inventory',
        effectiveCost: data.price,
        memberCost: data.price,
        nonMemberCost: data.price,
        shippingCategory: data.shippingCategory,
        personId: userStore.account?.personId,
        imageUrl: data.imageUrl,
        id: data.id,
        taxable: data.taxable,
        salesTaxRate: data.salesTaxRate,
      }
      await this.addItemWithQty(item, qty, data.quantity)
      // Recalculate the shipping price for the cart to include the new item
      await this.calculateShippingPrice()
    },
    addCharge(data, opts = {}) {
      const userStore = useUserStore()
      const item = {
        type: 'charge',
        feeType: data.feeType,
        itemId: data.itemId,
        glAccount: data.glAccount,
        glclass: data.glclass,
        glsubclass: data.glsubclass,
        personId: data.personId || userStore.account?.personId,
        title: data.title,
        subtitle: data.subtitle,
        memberCost: data.memberCost,
        nonMemberCost: data.nonMemberCost,
        parentId: data.parentId,
        removable: data.removable !== false,
      }
      return this.addItem(item)
    },
    updateRegistrationFees() {
      if (!siteConfig.features.registrationFee) return

      const userStore = useUserStore()

      const imageUrl = `/sites/${siteConfig.key}/icon.png`

      this.items
        .filter((item) => item.type === 'class')
        .forEach((item) => {
          // 2388 is high school app fee which is done using a class registration
          if (item.catalogId === 2388 || item.catalogId === 2833) return

          if (item.joinWaitlist) return

          if (!item.personId) return
          const firstDayOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]
          const paidRegFee = userStore.registrationFees.find(
            (fee) => fee.registrant_id === item.personId && fee.item_trans_date >= firstDayOfYear
          )

          if (paidRegFee) return

          const regFeeInCart = this.items.find(
            (feeItem) => feeItem.feeType === 'registrationFee' && feeItem.personId === item.personId
          )

          if (!regFeeInCart) {
            const regFeeItem = {
              itemId: siteConfig.features.registrationFee.itemId,
              personId: item.personId,
              glAccount: siteConfig.features.registrationFee.glAccount,
              feeType: 'registrationFee',
              title: 'Registration Fee',
              subtitle: siteConfig.features.registrationFee.subtitle,
              memberCost: siteConfig.features.registrationFee.memberCost,
              nonMemberCost: siteConfig.features.registrationFee.nonMemberCost,
              imageUrl,
              removable: false,
            }
            this.addCharge(regFeeItem)
          } else {
            regFeeInCart.removable = false
          }
        })

      this.items
        .filter((feeItem) => feeItem.feeType === 'registrationFee')
        .forEach((fee) => {
          const classItemsForPerson = this.items.filter(
            (item) => item.type === 'class' && item.personId === fee.personId
          )
          if (!classItemsForPerson.length) {
            this.removeItem(fee.cartId)
          }
        })
    },

    removeProcessingFee() {
      const processingFeeItem = this.items.find((item) => item.type === 'charge' && item.feeType === 'processingFee')
      if (processingFeeItem) {
        this.removeItem(processingFeeItem.cartId)
      }
    },
    addProcessingFee(cost) {
      if (!cost || cost <= 0) return
      this.addCharge({
        feeType: 'processingFee',
        title: getProcessingFeeTitle(siteConfig),
        subtitle: getProcessingFeeSubtitle(siteConfig),
        memberCost: cost,
        nonMemberCost: cost,
        ...getProcessingFeeGLFields(siteConfig),
        removable: false,
      })
    },
    updateProcessingFee(cost) {
      this.removeProcessingFee()
      this.addProcessingFee(cost)
    },
    addRental(rentalItem) {
      const userStore = useUserStore()

      const subtitle = [siteConfig.copy?.rentals?.name || 'Rental', ' for', rentalItem.dateFmt, rentalItem.timeSummary]
        .filter(Boolean)
        .join(' ')
      const item = {
        type: 'rental',
        locationId: rentalItem.locationId,
        personId: userStore.account?.personId,
        title: rentalItem.location,
        subtitle: subtitle,
        memberCost: rentalItem.cost,
        nonMemberCost: rentalItem.cost,
        membersOnly: rentalItem.membersOnly,
        inventoryId: rentalItem.inventoryId,
        code: rentalItem.code,
        rate: rentalItem.rate,
        quantity: rentalItem.quantity,
        cost: rentalItem.cost,
        glAccount: rentalItem.glAccount,
        startDate: rentalItem.dateFmt,
        startTime: rentalItem.startTimeFmt,
        endTime: rentalItem.endTimeFmt,
      }
      return this.addItem(item)
    },
    removeItem(cartId) {
      const alternativeShopStore = useAlternativeShopStore()
      if (!cartId) {
        this.updateShouldNotAddDonationAutomatically()
        this.items = this.items.filter((item) => item.type !== 'donation')
        this.saveItemsToLocalStorge()
        return
      }

      const cartItem = this.items.find((item) => item.cartId == cartId)
      const membersOnlyCartItems = this.items.filter((item) => !!item.membersOnly)

      if (cartItem.type === 'membership' && membersOnlyCartItems.length > 0) {
        // XXX TODO – in the future maybe show an alert to users that removing membership will remove membersOnly items
        const userStore = useUserStore()

        const otherMemberships = this.items.filter((item) => item.type === 'membership' && item.cartId !== cartId)

        // if (otherMemberships.length === 0) { //  && !userStore.isActiveMember
        // there are no other memberships in the cart, remove membersOnly items
        if (userStore.isActiveMember) {
          const newItems = this.items.filter((item) => item.cartId !== cartId)
          this.items = newItems
        } else if (otherMemberships.length === 0) {
          const newItems = this.items.filter((item) => item.cartId !== cartId && !item.membersOnly)
          this.items = newItems
        }
        // }
      } else {
        const newItems = this.items.filter((item) => item.cartId !== cartId && item.parentId !== cartId)
        this.items = newItems

        if (siteConfig.features.registrationFee) {
          const classesInCart = this.items.filter(
            (item) => item.type === 'class' && item.personId == cartItem.personId && !item.joinWaitlist
          )
          const regFeeInCart = this.items.find(
            (item) => item.feeType === 'registrationFee' && item.personId === cartItem.personId
          )

          if (classesInCart.length === 0 && regFeeInCart) {
            regFeeInCart.removable = true
          }
        }
      }

      if (cartItem.type === 'membership') {
        useClassStore().updateMembershipPricing()
      }

      if (cartItem.type === 'exhibition') {
        this.updateExhibitionPricing()
      }

      this.calculateShippingPrice()
      this.saveItemsToLocalStorge()
    },
    emptyCart() {
      this.items = []
      this.discountCode = null
      this.discounts = []
      localStorage.removeItem(`${siteConfig.key}Cart`)
    },
    filterOutBadItems() {
      const classStore = useClassStore()

      // 5 days in milliseconds
      const maxAge = 5 * 24 * 60 * 60 * 1000

      const filteredItems = this.items.filter((item) => {
        // First, get rid of any items older than 5 days
        if (item.addedAt) {
          const now = new Date()
          const timestamp = new Date(item.addedAt)
          const diff = now - timestamp

          if (diff > maxAge) {
            return false
          }
        }

        if (!classStore.loading && item.type === 'class') {
          const cls = classStore.filteredList.find((c) => (c.catalog_id = item.catalogId))
          if (!cls) {
            return false
          }
        }
        return true
      })

      if (this.items.length != filteredItems.length) {
        this.items = filteredItems
        this.saveItemsToLocalStorge()
      }
    },
    toggleCart(value) {
      this.openedCart = value
      if (!value) this.clearCartNotices()
    },
    addCartNotice(message) {
      if (!this.cartNotices.includes(message)) this.cartNotices.push(message)
    },
    clearCartNotices() {
      this.cartNotices = []
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot))
}
